import React, { useEffect } from 'react';
import logo           from '../../assets/logo.png';
import Overview       from './Overview';
import Services       from './Services';
import Approach       from './Approach';
import Colleges       from './Colleges';
import Brands         from './Brands';
import ChalkingBrands from './ChalkingBrands';
import Team           from './Team';
import Contact        from './Contact';

/* ── Shared: scroll reveal ── */
export function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal,.reveal-l,.reveal-r');
    const io  = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('in'); }),
      { threshold: 0.1 }
    );
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/* ── Shared: animated counter ── */
export function useCounter(target, inView) {
  const [val, setVal] = React.useState(0);
  const done = React.useRef(false);
  React.useEffect(() => {
    if (!inView || done.current) return;
    done.current = true;
    const n    = parseInt(String(target).replace(/\D/g, ''), 10);
    const step = Math.max(1, Math.ceil(n / 80));
    let cur    = 0;
    const t    = setInterval(() => {
      cur = Math.min(cur + step, n);
      setVal(cur);
      if (cur >= n) clearInterval(t);
    }, 16);
  }, [inView, target]);
  return val;
}

/* ── Arrow icon reused ── */
export const Arrow = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/* ════════════════════════════════
   HERO
════════════════════════════════ */
function Hero() {
  return (
    <section id="home"
      className="relative w-full min-h-screen flex flex-col items-center justify-center text-center px-6 overflow-hidden"
      style={{ background: 'linear-gradient(155deg,#111 55%,#1a0a0a 100%)' }}
    >
      {/* Ambient glows */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -bottom-20 -left-20 w-[600px] h-[600px] rounded-full opacity-20 blur-[80px]"
          style={{ background: 'radial-gradient(circle,rgba(255,0,50,.35),transparent 70%)' }} />
        <div className="absolute -top-20 -right-20 w-[500px] h-[500px] rounded-full opacity-15 blur-[80px]"
          style={{ background: 'radial-gradient(circle,rgba(5,131,242,.3),transparent 70%)' }} />
      </div>

      {/* Subtle grid */}
      <div className="pointer-events-none absolute inset-0 opacity-[.03]"
        style={{ backgroundImage:'linear-gradient(rgba(255,255,255,.5) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.5) 1px,transparent 1px)', backgroundSize:'64px 64px' }} />

      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Logo */}
        <div className="ha d1 mb-10 flex justify-center">
          <img src={logo} alt="Chalk and Charter" className="h-16 w-auto object-contain float" />
        </div>

        {/* Badge */}
        <div className="ha d2 flex justify-center mb-7">
          <span className="label label-red">Campus Marketing Agency — Nepal</span>
        </div>

        {/* Headline */}
        <h1 className="ha d3 heading-xl">
          Where <em>Brands</em> Meet<br />Campus Communities
        </h1>

        {/* Subtext */}
        <p className="ha d4 subtext mt-6 max-w-2xl mx-auto text-lg">
          We bridge the gap between brands and educational institutions
          through strategic BTL marketing that students actually notice.
        </p>

        {/* CTAs */}
        <div className="ha d4 mt-10 flex flex-wrap gap-4 justify-center">
          <a href="#services" className="btn-red">
            See Our Work <Arrow />
          </a>
          <a href="#contact" className="btn-outline">Talk to Us</a>
        </div>

        {/* Stat strip */}
        <div className="ha d4 mt-14 flex justify-center">
          <div className="hero-stat-strip flex-wrap gap-y-4 justify-center text-center">
            {[
              { val:'50+',    label:'Campus Partners'   },
              { val:'100+',   label:'Brand Clients'     },
              { val:'500K+',  label:'Students Reached'  },
              { val:'95%',    label:'Satisfaction Rate' },
            ].map((s, i) => (
              <React.Fragment key={s.label}>
                {i > 0 && <div className="vline hidden sm:block" />}
                <div>
                  <div className="text-white font-black text-xl" style={{ fontFamily:'Playfair Display,serif' }}>{s.val}</div>
                  <div className="text-white/40 text-xs mt-0.5 font-medium tracking-wide">{s.label}</div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/20 text-xs tracking-widest uppercase">
        <span>Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-white/15 to-transparent" />
      </div>
    </section>
  );
}

/* ════════════════════════════════
   HERO FEATURE CARDS
════════════════════════════════ */
const heroCards = [
  {
    accent:'accent-red', hover:'red-hover', icon:'red', color:'var(--red)',
    title:'Concept to Design',
    body:'Transforming innovative ideas into compelling campus experiences that leave a mark.',
    svg:<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 2a4 4 0 1 1 0 8 4 4 0 0 1 0-8zm0 10c4.42 0 8 1.79 8 4v2H4v-2c0-2.21 3.58-4 8-4z" fill="#FF0032"/></svg>,
  },
  {
    accent:'accent-blue', hover:'blue-hover', icon:'blue', color:'var(--blue)',
    title:'A Quiet Bridge To Connectivity',
    body:'Seamlessly connecting brands with student communities through authentic engagement.',
    svg:<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="5" cy="12" r="2.5" fill="#0583F2"/><circle cx="19" cy="12" r="2.5" fill="#0583F2"/><circle cx="12" cy="5" r="2.5" fill="#0583F2"/><path d="M7.5 12h9M12 7.5V12" stroke="#0583F2" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  },
  {
    accent:'accent-red', hover:'red-hover', icon:'red', color:'var(--red)',
    title:'Campus Design',
    body:'Crafting strategic brand presence across educational institutions with precision.',
    svg:<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 3l8 6.5v10a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9.5L12 3z" fill="#FF0032"/><path d="M9 22V16h6v6" stroke="#fff" strokeWidth="1.2" strokeLinecap="round"/></svg>,
  },
];

function HeroCards() {
  return (
    <section className="section bg-surface">
      <div className="max-w-content mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {heroCards.map((c, i) => (
            <div key={c.title} className={`card ${c.accent} ${c.hover} reveal d${i+1} p-8 group`}>
              <div className={`icon-box ${c.icon} mb-6`}>{c.svg}</div>
              <h3 className="text-white font-bold text-lg mb-3 leading-snug">{c.title}</h3>
              <p className="subtext text-sm leading-relaxed">{c.body}</p>
              <div className="mt-6 flex items-center gap-2 text-sm font-semibold transition-colors duration-200"
                style={{ color: c.color }}>
                Learn more <Arrow />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════
   ROOT
════════════════════════════════ */
export default function Home() {
  useReveal();
  return (
    <>
      <Hero />
      <HeroCards />
      <Overview />
      <Services />
      <Approach />
      <Colleges />
      <Brands />
      <ChalkingBrands />
      <Team />
      <Contact />
    </>
  );
}
